import type {
  AuditIssueDTO,
  AuditRecordDetailDTO,
  CreateAuditIssueDTO,
  UpdateAuditIssueDTO,
} from "@/modules/internal-audit/domain/contracts";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { formatDateTime } from "@/shared/lib/date-time";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  CheckSquare2,
  Plus,
  XSquare,
} from "lucide-react";
import { useState } from "react";
import {
  auditIssueKeys,
  useAuditIssuesByRecordItemQuery,
  useCreateAuditIssue,
  useDeleteAuditIssue,
  useUpdateAuditIssue,
} from "../api/queries";
import { AuditIssueForm } from "./AuditIssueForm";

interface AuditRecordDetailContentProps {
  record: AuditRecordDetailDTO;
  canManageIssues?: boolean;
}

export function AuditRecordDetailContent({
  record,
  canManageIssues = false,
}: AuditRecordDetailContentProps) {
  const queryClient = useQueryClient();
  const createIssueMutation = useCreateAuditIssue();
  const deleteIssueMutation = useDeleteAuditIssue();

  // State for modals
  const createIssueModal = useDisclosure();
  const viewIssuesModal = useDisclosure();
  const editIssueModal = useDisclosure();

  // Current selected item for creating/viewing issues
  const [selectedItem, setSelectedItem] = useState<
    AuditRecordDetailDTO["items"][number] | null
  >(null);
  const [editingIssue, setEditingIssue] = useState<AuditIssueDTO | null>(null);
  const [closingIssue, setClosingIssue] = useState<AuditIssueDTO | null>(null);

  // Update issue mutations
  const updateIssueMutation = useUpdateAuditIssue(editingIssue?.id || "");
  const closeIssueMutation = useUpdateAuditIssue(closingIssue?.id || "");

  const getResultBadge = (result: string) => {
    switch (result) {
      case "pass":
        return <Badge className="bg-green-100 text-green-800">ຜ່ານ</Badge>;
      case "fail":
        return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
      case "needs_improvement":
        return <Badge variant="secondary">ຕ້ອງປັບປຸງ</Badge>;
      default:
        return <Badge variant="outline">{result}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">ແລ້ວ</Badge>;
      case "draft":
        return <Badge variant="secondary">ຮ່າງ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getItemResultIcon = (result: "pass" | "fail" | "needs_improvement") => {
    if (result === "pass") {
      return <CheckSquare2 className="h-5 w-5 text-green-600" />;
    }
    if (result === "fail") {
      return <XSquare className="h-5 w-5 text-red-600" />;
    }
    return <AlertCircle className="h-5 w-5 text-yellow-600" />;
  };

  // Auto-fill logic for creating issue from item
  const getIssueInitialValues = (
    item: AuditRecordDetailDTO["items"][number],
  ): Partial<CreateAuditIssueDTO> => {
    return {
      departmentId: record.departmentId ?? undefined,
      auditRecordId: record.id,
      auditRecordItemId: item.id,
      title: `ປັນຫາ: ${item.title}`,
      description: item.notes || item.description || "",
      severity: item.result === "fail" ? "high" : "medium",
    };
  };

  const handleCreateIssue = (item: AuditRecordDetailDTO["items"][number]) => {
    setSelectedItem(item);
    createIssueModal.open();
  };

  const handleViewIssues = (item: AuditRecordDetailDTO["items"][number]) => {
    setSelectedItem(item);
    viewIssuesModal.open();
  };

  const handleCreateIssueSubmit = (
    values: CreateAuditIssueDTO | UpdateAuditIssueDTO,
  ) => {
    createIssueMutation.mutate(values as CreateAuditIssueDTO, {
      onSuccess: () => {
        createIssueModal.close();
        // Invalidate issues queries for this item
        if (selectedItem) {
          queryClient.invalidateQueries({
            queryKey: auditIssueKeys.byRecordItem(selectedItem.id),
          });
        }
      },
    });
  };

  const handleEditIssue = (issue: AuditIssueDTO) => {
    setEditingIssue(issue);
    viewIssuesModal.close();
    editIssueModal.open();
  };

  const handleUpdateIssueSubmit = (
    values: CreateAuditIssueDTO | UpdateAuditIssueDTO,
  ) => {
    if (!editingIssue) return;
    updateIssueMutation.mutate(values as UpdateAuditIssueDTO, {
      onSuccess: () => {
        editIssueModal.close();
        setEditingIssue(null);
        // Invalidate issues queries
        if (selectedItem) {
          queryClient.invalidateQueries({
            queryKey: auditIssueKeys.byRecordItem(selectedItem.id),
          });
        }
      },
    });
  };

  const handleCloseIssue = async (issue: AuditIssueDTO) => {
    const ok = await confirm({
      title: "ຢືນຢັນການປິດບັນຫາ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດບັນຫານີ້?",
      actionText: "ປິດບັນຫາ",
      cancelText: "ຍົກເລີກ",
    });

    if (!ok) return;

    try {
      setClosingIssue(issue);
      await closeIssueMutation.mutateAsync({
        status: "closed" as const,
        title: issue.title,
        description: issue.description,
        departmentId: issue.departmentId,
        severity: issue.severity,
        auditRecordId: issue.auditRecordId,
        auditRecordItemId: issue.auditRecordItemId || null,
        rootCause: issue.rootCause || null,
        assignedToPositionId: issue.assignedToPositionId || null,
        assignedToUserId: issue.assignedToUserId || null,
        dueDate: issue.dueDate || null,
      });

      setClosingIssue(null);

      // Invalidate issues queries
      if (selectedItem) {
        queryClient.invalidateQueries({
          queryKey: auditIssueKeys.byRecordItem(selectedItem.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: auditIssueKeys.all });
    } catch (_error) {
      setClosingIssue(null);
      // Error toast already handled by mutation
    }
  };

  const handleDeleteIssue = async (issue: AuditIssueDTO) => {
    const ok = await confirm({
      title: "ຢືນຢັນການລຶບ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບບັນຫານີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (!ok) return;

    try {
      await deleteIssueMutation.mutateAsync(issue.id);

      // Invalidate issues queries
      if (selectedItem) {
        queryClient.invalidateQueries({
          queryKey: auditIssueKeys.byRecordItem(selectedItem.id),
        });
      }

      // Close modals if the deleted issue was being edited
      if (editingIssue?.id === issue.id) {
        editIssueModal.close();
        setEditingIssue(null);
      }
    } catch (_error) {
      // Error toast already handled by mutation
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Section 1: ข้อมูลพื้นฐาน */}
      <Card data-tourid="record-detail-header">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>ຂໍ້ມູນບັນທຶກການກວດສອບ</span>
            {getResultBadge(record.overallResult)}
          </CardTitle>
        </CardHeader>
        <CardContent
          className="space-y-4"
          data-tourid="record-detail-basic-info"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜະແນການກວດສອບ
              </h3>
              <p className="text-sm">{record.auditPlanName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ປະເພດການກວດສອບ
              </h3>
              <p className="text-sm">{record.auditTypeName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ພະແນກ
              </h3>
              <p className="text-sm">{record.departmentName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີກວດສອບ
              </h3>
              <p className="text-sm">{formatDateTime(record.auditDate)}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜູ້ກວດສອບ
              </h3>
              <p className="text-sm">{record.auditorName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ສະຖານະ
              </h3>
              {getStatusBadge(record.status)}
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີສ້າງ
              </h3>
              <p className="text-sm">{formatDateTime(record.createdAt)}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີອັບເດດລ່າສຸດ
              </h3>
              <p className="text-sm">{formatDateTime(record.updatedAt)}</p>
            </div>
          </div>

          <div>
            <h3 className="mb-1 font-medium text-muted-foreground text-sm">
              ສິ່ງທີ່ກວດສອບ
            </h3>
            <p className="text-sm">{record.auditScope}</p>
          </div>

          {record.summary && (
            <div data-tourid="record-detail-summary">
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ສະຫຼຸບ
              </h3>
              <p className="whitespace-pre-wrap text-sm">{record.summary}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: รายการตรวจแต่ละข้อ */}
      {record.items && record.items.length > 0 && (
        <Card data-tourid="record-detail-items">
          <CardHeader>
            <CardTitle>ລາຍການກວດສອບ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {record.items
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((item) => (
                  <RecordItemCard
                    key={item.id}
                    item={item}
                    canManageIssues={canManageIssues}
                    onCreateIssue={() => handleCreateIssue(item)}
                    onViewIssues={() => handleViewIssues(item)}
                    getResultBadge={getResultBadge}
                    getItemResultIcon={getItemResultIcon}
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3: ไฟล์แนบ */}
      {record.attachments && record.attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>ຟາຍແນບ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {record.attachments.map((attachment) => {
                const src = attachment.fileKey || attachment.fileUrl;
                const resolved = resolveImageSrc(src);
                return (
                  <div
                    key={attachment.id}
                    className="group relative aspect-square overflow-hidden rounded-lg border"
                  >
                    {resolved ? (
                      <button
                        type="button"
                        className="block h-full w-full cursor-zoom-in p-0 text-left"
                        onClick={() =>
                          window.open(resolved, "_blank", "noopener,noreferrer")
                        }
                        aria-label="ເປີດຮູບໃນແທັບໃໝ່"
                      >
                        <AppImage
                          src={src}
                          alt={attachment.description || "Attachment"}
                          className="h-full w-full transition-opacity group-hover:opacity-80"
                          fit="cover"
                        />
                      </button>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-muted">
                        <span className="text-muted-foreground text-xs">
                          ບໍ່ພົບຮູບພາບ
                        </span>
                      </div>
                    )}
                    {attachment.description && (
                      <div className="absolute right-0 bottom-0 left-0 bg-black/50 p-2 text-white text-xs">
                        {attachment.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal: Create Issue */}
      <Dialog
        open={createIssueModal.isOpen}
        onOpenChange={createIssueModal.toggle}
      >
        <DialogContent className="!pb-0 !max-w-2xl">
          <ScrollArea className="max-h-[90vh] pr-2">
            <DialogHeader>
              <DialogTitle>ເພີ່ມບັນຫາ</DialogTitle>
              <DialogDescription>
                ສ້າງບັນຫາໃໝ່ສຳລັບລາຍການກວດສອບ: {selectedItem?.title}
              </DialogDescription>
            </DialogHeader>
            {selectedItem && (
              <AuditIssueForm
                initialValues={getIssueInitialValues(selectedItem)}
                onSubmit={handleCreateIssueSubmit}
                submitting={createIssueMutation.isPending}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Modal: View Issues */}
      <Dialog
        open={viewIssuesModal.isOpen}
        onOpenChange={viewIssuesModal.toggle}
      >
        <DialogContent className="!pb-0 !max-w-4xl">
          <ScrollArea className="max-h-[90vh] pr-2">
            <DialogHeader>
              <DialogTitle>ບັນຫາທີ່ພົບ</DialogTitle>
              <DialogDescription>
                ລາຍການບັນຫາຂອງ: {selectedItem?.title}
              </DialogDescription>
            </DialogHeader>
            {selectedItem && (
              <IssuesList
                recordItemId={selectedItem.id}
                onEditIssue={handleEditIssue}
                onCloseIssue={handleCloseIssue}
                onDeleteIssue={handleDeleteIssue}
                canManageIssues={canManageIssues}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Modal: Edit Issue */}
      <Dialog open={editIssueModal.isOpen} onOpenChange={editIssueModal.toggle}>
        <DialogContent className="!pb-0 !max-w-2xl">
          <ScrollArea className="max-h-[90vh] pr-2">
            <DialogHeader>
              <DialogTitle>ແກ້ໄຂບັນຫາ</DialogTitle>
            </DialogHeader>
            {editingIssue && (
              <AuditIssueForm
                initialValues={editingIssue}
                onSubmit={handleUpdateIssueSubmit}
                submitting={updateIssueMutation.isPending}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// RecordItemCard component
function RecordItemCard({
  item,
  canManageIssues,
  onCreateIssue,
  onViewIssues,
  getResultBadge,
  getItemResultIcon,
}: {
  item: AuditRecordDetailDTO["items"][number];
  canManageIssues: boolean;
  onCreateIssue: () => void;
  onViewIssues: () => void;
  getResultBadge: (result: string) => React.ReactNode;
  getItemResultIcon: (
    result: "pass" | "fail" | "needs_improvement",
  ) => React.ReactNode;
}) {
  const issuesQuery = useAuditIssuesByRecordItemQuery(item.id);
  const issues = issuesQuery.data || [];
  const hasIssues = issues.length > 0;

  // Only show buttons for fail or needs_improvement
  const showIssueButtons =
    canManageIssues &&
    (item.result === "fail" || item.result === "needs_improvement");

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-1 items-start gap-3">
          <div className="mt-0.5">{getItemResultIcon(item.result)}</div>
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-medium text-sm">
                {item.orderIndex + 1}. {item.title}
              </span>
              {getResultBadge(item.result)}
            </div>
            {item.description && (
              <p className="mb-2 text-muted-foreground text-sm">
                {item.description}
              </p>
            )}
            {item.notes && (
              <p className="mb-2 whitespace-pre-wrap text-muted-foreground text-xs">
                {item.notes}
              </p>
            )}

            {/* Issue buttons */}
            {showIssueButtons && (
              <div className="mt-3 flex gap-2">
                {hasIssues && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onViewIssues}
                    className="gap-1"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    ເບິ່ງບັນຫາ ({issues.length})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCreateIssue}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  {hasIssues ? "ເພີ່ມບັນຫາໃໝ່" : "ເພີ່ມບັນຫາ"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// IssuesList component
function IssuesList({
  recordItemId,
  onEditIssue,
  onCloseIssue,
  onDeleteIssue,
  canManageIssues,
}: {
  recordItemId: string;
  onEditIssue: (issue: AuditIssueDTO) => void;
  onCloseIssue: (issue: AuditIssueDTO) => void;
  onDeleteIssue: (issue: AuditIssueDTO) => void;
  canManageIssues: boolean;
}) {
  const issuesQuery = useAuditIssuesByRecordItemQuery(recordItemId);

  if (issuesQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">ກຳລັງໂຫຼດ...</div>
    );
  }

  if (!issuesQuery.data || issuesQuery.data.length === 0) {
    return <div className="p-4 text-center text-muted-foreground">ບໍ່ພົບບັນຫາ</div>;
  }

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return <Badge variant="destructive">ສຳຄັນຫຼາຍ</Badge>;
      case "high":
        return <Badge variant="destructive">ສູງ</Badge>;
      case "medium":
        return <Badge variant="secondary">ປານກາງ</Badge>;
      case "low":
        return <Badge variant="outline">ຕ່ຳ</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="destructive">ເປີດ</Badge>;
      case "in_progress":
        return <Badge variant="secondary">ກຳລັງແກ້ໄຂ</Badge>;
      case "resolved":
        return <Badge className="bg-green-100 text-green-800">ແກ້ໄຂແລ້ວ</Badge>;
      case "closed":
        return <Badge variant="outline">ປິດ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-3 p-4">
      {issuesQuery.data.map((issue) => {
        const isClosed = issue.status === "closed";

        return (
          <div key={issue.id} className="rounded-lg border p-4">
            <div className="mb-2 flex items-start justify-between gap-3">
              <h4 className="flex-1 font-medium text-sm">{issue.title}</h4>
              <div className="flex gap-2">
                {getSeverityBadge(issue.severity)}
                {getStatusBadge(issue.status)}
              </div>
            </div>
            <p className="line-clamp-2 text-muted-foreground text-xs">
              {issue.description}
            </p>
            {issue.assignedToUserName && (
              <p className="mt-2 text-muted-foreground text-xs">
                ມອບໝາຍໃຫ້: {issue.assignedToUserName}
              </p>
            )}

            {/* Action buttons */}
            {canManageIssues && (
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditIssue(issue)}
                  disabled={isClosed}
                >
                  ແກ້ໄຂ
                </Button>
                {!isClosed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCloseIssue(issue)}
                  >
                    ປິດບັນຫາ
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDeleteIssue(issue)}
                >
                  ລຶບ
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
